/**
 * useDeviceMotion — React hook for accelerometer + gyroscope data.
 *
 * Uses the DeviceMotion API to capture IMU data at ~60Hz.
 * Handles iOS permission prompt (requires user gesture).
 * Returns latest sensor readings for inclusion in bitblocks.
 *
 * Graceful degradation: returns null values on desktop or denied permission.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export interface MotionData {
  accelerometer: { x: number; y: number; z: number };
  gyroscope: { x: number; y: number; z: number };
}

interface UseDeviceMotionResult {
  /** Latest sensor readings (null if unavailable) */
  motion: MotionData | null;
  /** Whether device motion is supported and permitted */
  available: boolean;
  /** Request permission (required on iOS — must be called from user gesture) */
  requestPermission: () => Promise<boolean>;
  /** Whether permission has been granted */
  permitted: boolean;
}

export function useDeviceMotion(enabled: boolean = true): UseDeviceMotionResult {
  const [motion, setMotion] = useState<MotionData | null>(null);
  const [available, setAvailable] = useState(false);
  const [permitted, setPermitted] = useState(false);
  const motionRef = useRef<MotionData | null>(null);
  const lastUiUpdate = useRef(0);

  // Check if DeviceMotion API exists
  useEffect(() => {
    setAvailable(typeof DeviceMotionEvent !== 'undefined');
  }, []);

  // iOS requires explicit permission request from user gesture
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      // iOS 13+ requires permission request
      if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
        const result = await (DeviceMotionEvent as any).requestPermission();
        const granted = result === 'granted';
        setPermitted(granted);
        return granted;
      }
      // Non-iOS: permission is implicit
      setPermitted(true);
      return true;
    } catch {
      setPermitted(false);
      return false;
    }
  }, []);

  // Listen for motion events
  useEffect(() => {
    if (!enabled || !available || !permitted) return;

    const handler = (event: DeviceMotionEvent) => {
      const acc = event.accelerationIncludingGravity;
      const rot = event.rotationRate;

      const data: MotionData = {
        accelerometer: {
          x: acc?.x ?? 0,
          y: acc?.y ?? 0,
          z: acc?.z ?? 0,
        },
        gyroscope: {
          x: rot?.alpha ?? 0,
          y: rot?.beta ?? 0,
          z: rot?.gamma ?? 0,
        },
      };

      motionRef.current = data;
      // Throttle state updates to ~2Hz (every 500ms) to avoid 60Hz re-renders
      const now = Date.now();
      if (now - lastUiUpdate.current > 500) {
        setMotion(data);
        lastUiUpdate.current = now;
      }
    };

    window.addEventListener('devicemotion', handler, { passive: true });

    return () => {
      window.removeEventListener('devicemotion', handler);
    };
  }, [enabled, available, permitted]);

  return { motion, available, permitted, requestPermission };
}

/**
 * Get the latest motion data without triggering a re-render.
 * Useful in high-frequency loops (e.g., MediaPipe frame callback).
 */
export function getLatestMotion(motionRef: React.RefObject<MotionData | null>): MotionData | undefined {
  return motionRef.current ?? undefined;
}
